// Editing vector paths point by point while keeping the node's box tight around the path.
import type { Editor } from './editor.ts';
import type { Document } from './document.ts';
import { pointInPolygon } from './hittest.ts';
import type { NodeId, Vec2 } from './types.ts';
import { parseSvgPath, pathToSvg, pathBounds, localTransform, apply, transformPath, type PathCmd } from './geometry.ts';

export interface Anchor {
  /** index of the command whose endpoint this anchor is */
  cmd: number;
  p: Vec2;
  /** incoming control point (x2,y2 of this C command) */
  hin?: Vec2;
  /** outgoing control point (x1,y1 of the next C command) */
  hout?: Vec2;
}

export function anchorsOf(cmds: PathCmd[]): Anchor[] {
  const out: Anchor[] = [];
  cmds.forEach((c, i) => {
    if (c.c === 'Z') return;
    const a: Anchor = { cmd: i, p: { x: c.x, y: c.y } };
    if (c.c === 'C') a.hin = { x: c.x2, y: c.y2 };
    const next = cmds[i + 1];
    if (next?.c === 'C') a.hout = { x: next.x1, y: next.y1 };
    out.push(a);
  });
  return out;
}

/** Move an anchor (with its handles) or one of its handles to a new local position. */
export function moveAnchorPart(cmds: PathCmd[], cmdIndex: number, part: 'p' | 'hin' | 'hout', to: Vec2, mirror = true): PathCmd[] {
  const out = cmds.map((c) => ({ ...c })) as PathCmd[];
  const c = out[cmdIndex];
  if (!c || c.c === 'Z') return out;
  const next = out[cmdIndex + 1];
  if (part === 'p') {
    const dx = to.x - c.x,
      dy = to.y - c.y;
    c.x = to.x;
    c.y = to.y;
    if (c.c === 'C') {
      c.x2 += dx;
      c.y2 += dy;
    }
    if (next?.c === 'C') {
      next.x1 += dx;
      next.y1 += dy;
    }
    // closed subpath: the M point coincides with the last point before Z
    if (c.c === 'M') {
      for (let j = cmdIndex + 1; j < out.length; j++) {
        const z = out[j];
        if (z.c === 'Z') {
          const last = out[j - 1];
          if (last && last.c !== 'Z' && last.c !== 'M' && Math.abs(last.x - (to.x - dx)) < 1e-6 && Math.abs(last.y - (to.y - dy)) < 1e-6) {
            last.x = to.x;
            last.y = to.y;
            if (last.c === 'C') {
              last.x2 += dx;
              last.y2 += dy;
            }
          }
          break;
        }
        if (z.c === 'M') break;
      }
    }
  } else if (part === 'hin' && c.c === 'C') {
    c.x2 = to.x;
    c.y2 = to.y;
    if (mirror && next?.c === 'C') {
      next.x1 = 2 * c.x - to.x;
      next.y1 = 2 * c.y - to.y;
    }
  } else if (part === 'hout' && next?.c === 'C') {
    next.x1 = to.x;
    next.y1 = to.y;
    if (mirror && c.c === 'C') {
      c.x2 = 2 * c.x - to.x;
      c.y2 = 2 * c.y - to.y;
    }
  }
  return out;
}

/** Remove the anchor at command index (keeps the path valid). */
export function deleteAnchor(cmds: PathCmd[], cmdIndex: number): PathCmd[] {
  const out = cmds.map((c) => ({ ...c })) as PathCmd[];
  const c = out[cmdIndex];
  if (!c || c.c === 'Z') return out;
  if (c.c === 'M') {
    const next = out[cmdIndex + 1];
    if (next && next.c !== 'Z' && next.c !== 'M') out[cmdIndex + 1] = { c: 'M', x: next.x, y: next.y };
    out.splice(cmdIndex, 1);
    return out;
  }
  out.splice(cmdIndex, 1);
  return out;
}

/** Remove several anchors at once (command indices, any order). */
export function deleteAnchors(cmds: PathCmd[], cmdIndices: number[]): PathCmd[] {
  let out = cmds;
  for (const i of [...new Set(cmdIndices)].sort((a, b) => b - a)) out = deleteAnchor(out, i);
  return out;
}

/** Anchors of a vector whose page position falls inside a polygon (page coords), as command indices. */
export function anchorsInPolygon(doc: Document, id: NodeId, poly: Vec2[]): number[] {
  const n = doc.get(id);
  if (!n || poly.length < 3) return [];
  const m = doc.worldTransform(id);
  return anchorsOf(pathCommands(n.path))
    .filter((a) => pointInPolygon(apply(m, a.p), poly))
    .map((a) => a.cmd);
}

/**
 * Move several anchors together: each goes to its start position (page coords) plus the drag
 * delta. Targets are absolute, so calling this on every pointer move never accumulates error.
 */
export function moveAnchorsBy(editor: Editor, id: NodeId, starts: { cmd: number; page: Vec2 }[], delta: Vec2) {
  const n = editor.doc.get(id);
  if (!n) return;
  let cmds = pathCommands(n.path);
  for (const s of starts) cmds = moveAnchorPart(cmds, s.cmd, 'p', editor.doc.toLocal(id, { x: s.page.x + delta.x, y: s.page.y + delta.y }));
  setVectorPath(editor, id, cmds, 'Move points');
}

/**
 * Write a new path (local coordinates of the node) and re-fit the node box so the path
 * starts at 0,0 — keeping every point exactly where it was on the page.
 */
export function setVectorPath(editor: Editor, id: NodeId, cmds: PathCmd[], label = 'Edit path') {
  const n = editor.doc.must(id);
  const b = pathBounds(cmds);
  const w = Math.max(0.01, b.width),
    h = Math.max(0.01, b.height);
  const shifted = transformPath(cmds, [1, 0, 0, 1, -b.x, -b.y]);
  // new box centre must map to where the old transform puts the path-bounds centre
  const c = apply(localTransform(n), { x: b.x + w / 2, y: b.y + h / 2 });
  editor.update(id, { path: pathToSvg(shifted), x: c.x - w / 2, y: c.y - h / 2, width: w, height: h }, label);
}

export function pathCommands(path: string | undefined): PathCmd[] {
  return parseSvgPath(path ?? '');
}
